/**
 * TrophyGlb3D — the rotating gold trophy loaded from the company's uploaded GLB
 * (trophy.glb). Sits on the peak of the mountain on BOTH the Company View goal
 * card and the Dashboard King of the Mountain. Transparent canvas, slow Y
 * rotation, fills its container.
 *
 * COLOUR IS LEFT UNTOUCHED. The model's gold comes from its own baked texture,
 * so we do NOT modify any material (no metalness/roughness/colour changes) and
 * we light it with neutral white light + a neutral studio environment. The env
 * map is only there so the fully-metallic surface has something to reflect
 * (metal with no environment renders black) — it does not change the base colour.
 */
/* eslint-disable react/no-unknown-property -- three.js / r3f use lowercase JSX props */
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import Box from "@mui/material/Box";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment";
import trophyGlb from "../../assets/models/trophy.glb";

// Neutral studio environment so the metallic surface reflects something and reads
// as its true (gold) colour. Does not tint or recolour the model.
function NeutralEnvironment() {
  const { gl, scene } = useThree();
  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04);
    scene.environment = env.texture;
    return () => {
      scene.environment = null;
      env.texture.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);
  return null;
}

function TrophyModel() {
  const groupRef = useRef();
  const { scene } = useGLTF(trophyGlb);

  // Clone so THIS instance owns its scene graph. useGLTF returns ONE shared
  // scene object, and a three.js object can only live in one place in the tree
  // — so a second trophy on the page (there are two peak overlays), or a
  // StrictMode double-mount, makes the second <primitive> steal the scene from
  // the first and it blanks out. That is the "renders then vanishes" bug.
  // Cloning gives each instance its own copy (materials/geometry are shared,
  // which is fine since we never modify them).
  const model = useMemo(() => scene.clone(true), [scene]);

  // Auto-fit + center so the trophy fills the canvas consistently. Measured off
  // an identity transform so a prior scale never contaminates the measurement.
  // NOTE: materials are intentionally left exactly as authored.
  const { fitScale, offset } = useMemo(() => {
    try {
      model.position.set(0, 0, 0);
      model.rotation.set(0, 0, 0);
      model.scale.set(1, 1, 1);
      model.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const s = 2.0 / maxDim;
      return { fitScale: s, offset: [-center.x * s, -center.y * s, -center.z * s] };
    } catch {
      return { fitScale: 1, offset: [0, 0, 0] };
    }
  }, [model]);

  // GLB has no baked animation — spin it ourselves, slow and smooth.
  useFrame((_, delta) => {
    if (groupRef.current) groupRef.current.rotation.y += delta * 0.7;
  });

  return (
    <group ref={groupRef}>
      <group scale={fitScale} position={offset}>
        <primitive object={model} />
      </group>
    </group>
  );
}

const TrophyGlb3D = ({ size = "100%" }) => (
  <Box sx={{ width: size, height: size, flexShrink: 0, pointerEvents: "none" }}>
    <Canvas
      camera={{ position: [0, 0.1, 5.0], fov: 30 }}
      gl={{ alpha: true, antialias: true }}
      dpr={[1, 2]}
      style={{ background: "transparent", overflow: "visible" }}
    >
      {/* Neutral white lighting — does not tint the trophy's colour. */}
      <ambientLight intensity={0.5} />
      <directionalLight position={[3, 5, 4]} intensity={1.1} />
      <directionalLight position={[-3, 2, -2]} intensity={0.5} />
      <Suspense fallback={null}>
        <NeutralEnvironment />
        <TrophyModel />
      </Suspense>
    </Canvas>
  </Box>
);

/* eslint-enable react/no-unknown-property */

export default TrophyGlb3D;

// Pre-cache so the first paint doesn't flash empty.
useGLTF.preload(trophyGlb);
